import { useState, useMemo, useCallback, useEffect, useRef, type MouseEvent } from 'react'
import { useReactFlow, type Node, type Edge, type OnSelectionChangeFunc } from '@xyflow/react'
import type { NetworkNode, NetworkEdge, NetworkNodeData, GraphViewState } from '../types'
import { FIT_VIEW_MAX_ZOOM } from 'uikit/graphs/default'
import { FIT_VIEW_DURATION } from '../constants'

export interface UseGraphViewOptions {
  nodes: NetworkNode[]
  edges: NetworkEdge[]
}

export type UseGraphViewReturn = GraphViewState

/**
 * Hook for managing graph view state.
 * Handles selection, drawer visibility, viewport adjustments, and interaction handlers.
 * Extensible for future view concerns (hover, focus, zoom level, filters, etc.)
 */
export function useGraphView({ nodes, edges }: UseGraphViewOptions): UseGraphViewReturn {
  const { fitView, setNodes } = useReactFlow()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null)
  const skipNextFitViewRef = useRef(false)

  // Add selection state to edges
  const edgesWithSelection = useMemo(() => {
    return edges.map(e => ({
      ...e,
      selected: e.id === selectedEdgeId,
    }))
  }, [edges, selectedEdgeId])

  // Get the currently selected node (for details panel - single selection only)
  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null
    return nodes.find(n => n.id === selectedNodeId) || null
  }, [nodes, selectedNodeId])

  // Get the currently selected edge
  const selectedEdge = useMemo(() => {
    if (!selectedEdgeId) return null
    return edges.find(e => e.id === selectedEdgeId) || null
  }, [edges, selectedEdgeId])

  // Node options for search/select dropdown
  const nodeOptions = useMemo(() => {
    return nodes.map(n => {
      const data = n.data as NetworkNodeData
      return {
        id: n.id,
        identifier: data.identifierKey,
        displayName: data.displayName,
        nodeType: data.nodeType,
      }
    })
  }, [nodes])

  // Auto-fit view when drawer opens/closes (skip if triggered by search selection)
  useEffect(() => {
    if (skipNextFitViewRef.current) {
      skipNextFitViewRef.current = false
      return
    }
    const timer = setTimeout(() => {
      fitView({ padding: 0.1, duration: 200, maxZoom: FIT_VIEW_MAX_ZOOM })
    }, 350)
    return () => clearTimeout(timer)
  }, [isDrawerOpen, fitView])

  const handleNodeClick = useCallback((_event: MouseEvent, node: Node) => {
    setSelectedNodeId(node.id)
    setSelectedEdgeId(null)
    setIsDrawerOpen(true)
  }, [])

  const handleEdgeClick = useCallback((_event: MouseEvent, edge: Edge) => {
    setSelectedEdgeId(edge.id)
    setSelectedNodeId(null)
    setIsDrawerOpen(true)
  }, [])

  const handleDrawerToggle = useCallback(() => {
    setIsDrawerOpen(prev => !prev)
  }, [])

  const handleNodeSelect = useCallback(
    (nodeId: string) => {
      const node = nodes.find(n => n.id === nodeId)
      if (node) {
        setSelectedNodeId(nodeId)
        setSelectedEdgeId(null)
        skipNextFitViewRef.current = true
        setIsDrawerOpen(true)
        // Apply selection after a short delay, then start fitView
        // This ensures React Flow processes the selection before animating
        setTimeout(() => {
          setNodes(nds => nds.map(n => ({ ...n, selected: n.id === nodeId })))
          fitView({ nodes: [node], padding: 0.5, duration: FIT_VIEW_DURATION })
        }, 0)
      }
    },
    [nodes, fitView, setNodes],
  )

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null)
    setSelectedEdgeId(null)
  }, [])

  /**
   * Handle selection changes from React Flow's native multi-select.
   * Shows details panel only for single node selection.
   */
  const onSelectionChange: OnSelectionChangeFunc = useCallback(({ nodes: selectedNodes }) => {
    if (selectedNodes.length === 1) {
      // Single node selected - show details panel
      setSelectedNodeId(selectedNodes[0].id)
      setSelectedEdgeId(null)
      setIsDrawerOpen(true)
    } else if (selectedNodes.length > 1) {
      // Multiple nodes selected - hide details panel
      setSelectedNodeId(null)
      setSelectedEdgeId(null)
      setIsDrawerOpen(false)
    }
    // Note: selectedNodes.length === 0 is handled by handlePaneClick
  }, [])

  return {
    selectedNode,
    selectedEdge,
    isDrawerOpen,
    edgesWithSelection,
    nodeOptions,
    handleNodeClick,
    handleEdgeClick,
    handleDrawerToggle,
    handleNodeSelect,
    handlePaneClick,
    onSelectionChange,
  }
}
